import './App.css';
import React, { Suspense, lazy } from 'react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import FeedbackFAB from './components/FeedbackFAB';
import { RequireAuth, useFirebaseAuth } from './auth';
import { createLogger } from './utils/logger';

// Lazy load page components for code splitting
const DashboardPage = lazy(() => import('./components/pages/DashboardPage'));
const MonitorsPage = lazy(() => import('./components/pages/MonitorsPage'));
const IncidentsPage = lazy(() => import('./components/pages/IncidentsPage'));
const StatusPagesPage = lazy(() => import('./components/pages/StatusPagesPage'));
const TeamsPage = lazy(() => import('./components/pages/TeamsPage'));
const SettingsPage = lazy(() => import('./components/pages/SettingsPage'));
const PublicStatusPage = lazy(() => import('./components/PublicStatusPage'));
const LandingPage = lazy(() => import('./components/LandingPage'));

const logger = createLogger('App');

// Map URL paths to nav items
const pathToNavMap = {
  '/dashboard': 'dashboard',
  '/monitors': 'monitors',
  '/incidents': 'incidents',
  '/status-pages': 'status-pages',
  '/teams': 'teams',
  '/settings': 'settings'
};

// Map nav items to URL paths
const navToPathMap = {
  'dashboard': '/dashboard',
  'monitors': '/monitors',
  'incidents': '/incidents',
  'status-pages': '/status-pages',
  'teams': '/teams',
  'settings': '/settings'
};

function App() {
  const { user, loading } = useFirebaseAuth();
  const [activeNav, setActiveNav] = React.useState('dashboard');
  const [monitors, setMonitors] = React.useState([]);
  const [histories, setHistories] = React.useState({});
  const [monitorFilter, setMonitorFilter] = React.useState('all');
  const [statusPageSlug, setStatusPageSlug] = React.useState(null);

  // Get nav item from current path
  const getNavFromPath = React.useCallback((path) => {
    // Remove query parameters and hash fragments
    const pathWithoutQuery = path.split('?')[0].split('#')[0];
    // Remove trailing slash
    const cleanPath = pathWithoutQuery.replace(/\/$/, '') || '/';
    return pathToNavMap[cleanPath] || 'dashboard';
  }, []);

  // Check if we're on a public status page route and sync navigation
  const checkRoute = React.useCallback(() => {
    const path = window.location.pathname;
    const statusPageMatch = path.match(/^\/status\/([^/]+)$/);
    if (statusPageMatch) {
      setStatusPageSlug(statusPageMatch[1]);
    } else {
      setStatusPageSlug(null);
      // Sync navigation with URL
      const navItem = getNavFromPath(path);
      setActiveNav(navItem);
      
      // If authenticated and on "/", redirect to dashboard
      if (user && path === '/') {
        window.history.replaceState({}, '', '/dashboard');
        setActiveNav('dashboard');
      }
    }
  }, [getNavFromPath, user]);

  React.useEffect(() => {
    checkRoute();
    // Listen for browser navigation (back/forward)
    window.addEventListener('popstate', checkRoute);
    return () => window.removeEventListener('popstate', checkRoute);
  }, [checkRoute]);

  // Redirect to dashboard when user becomes authenticated
  // Add a small delay to ensure popup sign-in flow completes
  React.useEffect(() => {
    if (!loading && user && window.location.pathname === '/') {
      // Small delay to ensure popup sign-in completes before redirect
      const timer = setTimeout(() => {
        window.history.replaceState({}, '', '/dashboard');
        setActiveNav('dashboard');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, loading]);

  // Load monitors and histories for notifications
  React.useEffect(() => {
    async function loadData() {
      try {
        const { fetchJSON } = await import('./apiFetch');
        const { monitors: monitorsData } = await fetchJSON('/api/monitors').catch(() => ({ monitors: [] }));
        setMonitors(monitorsData || []);
        
        if (monitorsData && monitorsData.length > 0) {
          const ids = monitorsData.map((m) => m.id);
          const historyPromises = ids.map((id) =>
            fetchJSON(`/api/history?id=${encodeURIComponent(id)}&limit=720`)
              .then(res => ({ id, rows: res.rows || [] }))
              .catch(() => ({ id, rows: [] }))
          );
          const historyResults = await Promise.all(historyPromises);
          const histMap = {};
          for (const r of historyResults) {
            histMap[r.id] = r.rows || [];
          }
          setHistories(histMap);
        }
      } catch (error) {
        logger.error('Failed to load data for notifications:', error);
      }
    }

    // Only load data if we're authenticated (not on public status page)
    if (!statusPageSlug) {
      loadData();
      // Refresh every minute
      const interval = setInterval(loadData, 60_000);
      return () => clearInterval(interval);
    }
  }, [statusPageSlug]);

  function handleNavigate(page, options = {}) {
    setActiveNav(page);
    // Update URL to match the page
    const path = navToPathMap[page] || '/dashboard';
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
    if (options.filter) {
      setMonitorFilter(options.filter);
    } else if (page !== 'monitors') {
      // Reset filter when navigating away from monitors page
      setMonitorFilter('all');
    }
  }

  function renderPage(activeNav) {
    const PageContent = (() => {
      switch (activeNav) {
        case 'dashboard':
          return <DashboardPage onNavigate={handleNavigate} />;
        case 'monitors':
          return <MonitorsPage initialFilter={monitorFilter} onFilterChange={setMonitorFilter} />;
        case 'incidents':
          return <IncidentsPage />;
        case 'status-pages':
          return <StatusPagesPage />;
        case 'teams':
          return <TeamsPage />;
        case 'settings':
          return <SettingsPage />;
        default:
          return <DashboardPage onNavigate={handleNavigate} />;
      }
    })();

    return (
      <Suspense fallback={
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          color: 'var(--text-muted)'
        }}>
          Loading...
        </div>
      }>
        {PageContent}
      </Suspense>
    );
  }

  // If we're on a public status page, render it without the app layout (no auth required)
  if (statusPageSlug) {
    return (
      <Suspense fallback={
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--bg)',
          color: 'var(--text-muted)'
        }}>
          Loading status page...
        </div>
      }>
        <PublicStatusPage slug={statusPageSlug} />
      </Suspense>
    );
  }

  // Show landing page at "/" when not authenticated
  if (!loading && !user && window.location.pathname === '/') {
    return (
      <Suspense fallback={
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--bg)',
          color: 'var(--text-muted)'
        }}>
          Loading...
        </div>
      }>
        <LandingPage />
      </Suspense>
    );
  }

  // Protect all other routes with authentication
  return (
    <RequireAuth>
      <AppContent
        activeNav={activeNav}
        handleNavigate={handleNavigate}
        monitorFilter={monitorFilter}
        setMonitorFilter={setMonitorFilter}
        monitors={monitors}
        histories={histories}
        renderPage={renderPage}
      />
    </RequireAuth>
  );
}

function AppContent({ activeNav, handleNavigate, monitorFilter, setMonitorFilter, monitors, histories, renderPage }) {
  const { user } = useFirebaseAuth();

  return (
    <div className="app-layout">
      <Sidebar activeItem={activeNav} onNavigate={handleNavigate} />
      <div className="main-content">
        <Header
          user={user}
          onOpenAccountSettings={() => handleNavigate('settings')}
          monitors={monitors}
          histories={histories}
          onNavigate={handleNavigate}
        />
        {renderPage(activeNav)}
      </div>
      <FeedbackFAB />
    </div>
  );
}

export default App;
